/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計展開の前に通す確認の手順。pt 超過の警告・開発リーダーの引き受け確認・基本設計の開始/終了予定日（カレンダーで選ぶ）の受け取りを、描画から切り離した純関数で決める
 * @graph-connects none
 */

/**
 * 展開前に通す確認の段。
 *
 * - `pointWarning` — 合計 pt が上限を超えている（チャンクに分ける方がよい）
 * - `leaderSwap`   — 押した人が母票の開発リーダーではない
 * - `schedule`     — 基本設計の開始 / 終了予定日の入力（常に通る）
 */
export type ExpandStep = 'pointWarning' | 'leaderSwap' | 'schedule';

/**
 * 通すべき段を順に返す。**順序は固定**で、警告 → 引き受け確認 → 日付の入力。
 *
 * 日付を先に書かせてから「やっぱり展開できません」と返すと入力が無駄になるので、
 * 断られる可能性のある確認を先に置く。
 * @graph-connects none
 */
export function expandSteps(input: {
  /** 合計 pt が上限を超えているか。 */
  overPointLimit: boolean;
  /** 押した人が母票の開発リーダーか。 */
  isDevLeader: boolean;
}): ExpandStep[] {
  const steps: ExpandStep[] = [];
  if (input.overPointLimit) steps.push('pointWarning');
  if (!input.isDevLeader) steps.push('leaderSwap');
  steps.push('schedule');
  return steps;
}

/** カレンダー欄が返す形（`YYYY-MM-DD`）だけを受け取る。 @graph-connects none */
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * 日付欄の値（`YYYY-MM-DD`）を読む。受け取れない文字列は null。
 *
 * 月末を越えた日付（2026-02-31 等）も弾く。`Date` に食わせると 3/3 へ繰り上がって
 * 黙って別の日になるため、組み立て直した値と突き合わせて確かめる。カレンダーから
 * 選ぶ限りそんな値は来ないが、ここは Backlog へ渡す値の最後の関所なので確かめておく。
 * @graph-connects none
 */
export function parseIsoDate(value: string): string | null {
  const matched = ISO_DATE_RE.exec(value.trim());
  if (matched === null) return null;
  const iso = matched[0];
  const parsed = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().slice(0, 10) === iso ? iso : null;
}

/** 日付入力の受け取り結果。 */
export type ScheduleResult =
  | { ok: true; startDate: string; endDate: string }
  | { ok: false; reason: 'missing' | 'order' };

/**
 * 開始 / 終了予定日を受け取れるか判定する。両方選ばれていること、終了が開始より
 * 前でないことを見る。
 * @graph-connects parseIsoDate [calls] 日付欄の値の解釈
 */
export function readSchedule(start: string, end: string): ScheduleResult {
  const startDate = parseIsoDate(start);
  const endDate = parseIsoDate(end);
  if (startDate === null || endDate === null) return { ok: false, reason: 'missing' };
  if (endDate < startDate) return { ok: false, reason: 'order' };
  return { ok: true, startDate, endDate };
}
